<script setup lang="ts">
import { computed, inject } from "vue";
import type { TagGroupProps } from "~/components/clarity-ui/Tag/TagGroup";

export interface TagProps {
  /** Color value to chose from. */
  color?:
    | "information"
    | "success"
    | "failure"
    | "cancel"
    | "new"
    | "deal"
    | "highlight"
    | "light"
    | "dark";
  outlined?: boolean;
  size?: "small" | "medium";
}

// A boolean defaults always to false in Vue. To be able to use the parent's value, it needs to default
// to undefined.
// outlined is not set -> parent's value for outlined or parent's default value for outlined will be used
// outlined is set -> child's value for outlined will be used regardless of parent's value for outlined
const { color, outlined = undefined, size } = defineProps<TagProps>();

const tagGroupProps = inject<TagGroupProps>("tagGroupContext", {});

const computedColor = computed(() => color || tagGroupProps?.color || "information");
const computedOutlined = computed(() => outlined ?? tagGroupProps?.outlined ?? false);
const computedSize = computed(() => size || tagGroupProps?.size || "medium");
</script>

<template>
  <span
    class="inline-block rounded-sm border px-1.5 py-1 text-sm leading-none"
    :class="[
      `tag-${computedColor}`,
      {
        'tag-outlined': computedOutlined,
        [`tag-${computedSize}`]: computedSize !== 'medium',
      },
    ]"
    role="status"
    data-testid="tag"
    ><slot
  /></span>
</template>

<style lang="postcss" scoped>
.tag-information {
  @apply border-grey-blue bg-grey-blue text-dark-blue;

  &.tag-outlined {
    @apply border-dark-blue bg-transparent;
  }
}

.tag-success {
  @apply border-light-green bg-light-green text-lime-800;

  &.tag-outlined {
    @apply border-lime-700 bg-transparent text-lime-700;
  }
}

.tag-failure {
  @apply border-red-100 bg-red-100 text-red-700;

  &.tag-outlined {
    @apply border-red-600 bg-transparent text-red-600;
  }
}

.tag-cancel {
  @apply border-light-grey bg-light-grey text-neutral-600;

  &.tag-outlined {
    @apply border-neutral-600 bg-transparent;
  }
}

.tag-new {
  @apply border-light-medium-blue bg-light-medium-blue text-black;

  &.tag-outlined {
    @apply bg-transparent;
  }
}

.tag-deal {
  @apply border-light-pink bg-light-pink text-black;

  &.tag-outlined {
    @apply border-light-pink bg-transparent;
  }
}

.tag-highlight {
  @apply border-highlight-green bg-highlight-green text-black;

  &.tag-outlined {
    @apply bg-transparent;
  }
}

.tag-light {
  @apply border-white bg-white text-black;

  &.tag-outlined {
    @apply bg-transparent text-white backdrop-blur-lg;
  }
}

.tag-dark {
  @apply border-black bg-black text-white;

  &.tag-outlined {
    @apply bg-transparent text-black backdrop-blur-lg;
  }
}

.tag-small {
  @apply text-xs leading-none;
}
</style>
